import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const { login } = useAuth();
  const nav = useNavigate();
  const processed = useRef(false);
  const [message, setMessage] = useState("Verifying your Google account…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("Missing Google session. Please try again.");
      nav("/login/customer", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", {
          session_id: sessionId,
        });
        // NEW: If this is a new Google user, we need to collect a phone
        // number so the Customer ID uses the same "last-4-of-phone + 5
        // random" format as normal signups.
        if (data?.needs_phone) {
          const params = new URLSearchParams({
            google: data.pending_token,
            email: data.email || "",
            name: data.name || "",
          });
          // Restore referral code that was stashed before the Google redirect
          let stashedRef = null;
          try { stashedRef = sessionStorage.getItem("zola_pending_ref"); }
          catch { /* ignore */ }
          if (stashedRef) {
            params.set("ref", stashedRef);
            try { sessionStorage.removeItem("zola_pending_ref"); }
            catch { /* ignore */ }
          }
          window.history.replaceState(null, "", window.location.pathname);
          setMessage("One more step — please confirm your phone number.");
          nav(`/join?${params.toString()}`, { replace: true });
          return;
        }
        // Existing member — still clear any stray stashed ref
        try { sessionStorage.removeItem("zola_pending_ref"); }
        catch { /* ignore */ }
        login(data.access_token, { ...data.user, role: "customer" });
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
        nav("/customer", { replace: true });
      } catch (e) {
        const msg = formatError(e);
        setMessage(msg);
        toast.error(msg);
        setTimeout(() => nav("/login/customer", { replace: true }), 2500);
      }
    })();
  }, [login, nav]);

  return (
    <div
      className="min-h-screen bg-[color:var(--zola-navy)] text-white grid place-items-center px-6"
      data-testid="google-callback-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="mx-auto h-12 w-12 rounded-md bg-[color:var(--zola-gold)] text-[color:var(--zola-navy)] grid place-items-center">
          <Plane className="h-5 w-5" strokeWidth={2.4} />
        </div>
        <h1 className="mt-6 font-display text-3xl">
          Signing you in with <span className="gold">Google</span>
        </h1>
        <p className="mt-3 text-white/70 text-sm">{message}</p>
      </div>
    </div>
  );
}
